export const GRAPHQL_READ_CACHE = "graphql-read-cache";

export const GRAPHQL_HISTORY_READ_CACHE = "graphql-history-read-cache";

export const GRAPHQL_REFERENCE_READ_CACHE = "graphql-reference-read-cache";

export const GRAPHQL_QUANTITY_READ_CACHE = "graphql-quantity-read-cache";

export const GRAPHQL_LOCATION_TREE_READ_CACHE = "graphql-location-tree-read-cache";

export const GRAPHQL_MATERIAL_CATALOGUE_READ_CACHE = "graphql-material-catalogue-read-cache";

export const READ_CACHE_NAMES: readonly string[] = [
	GRAPHQL_READ_CACHE,
	GRAPHQL_HISTORY_READ_CACHE,
	GRAPHQL_REFERENCE_READ_CACHE,
	GRAPHQL_QUANTITY_READ_CACHE,
	GRAPHQL_LOCATION_TREE_READ_CACHE,
	GRAPHQL_MATERIAL_CATALOGUE_READ_CACHE
];

export const CATALOG_CACHE_OPERATIONS: readonly string[] = [
	"GetItemsForSelector",
	"GetCustomerLocationsForSelector",
	"GetVerifyOrderNumbers"
];

export const CACHEABLE_READ_OPERATIONS: readonly string[] = CATALOG_CACHE_OPERATIONS;

export const READ_CACHE_MAX_AGE_SECONDS = 24 * 60 * 60;

export const READ_CACHE_MAX_ENTRIES = 60;

export const REFERENCE_CACHE_OPERATIONS: readonly string[] = ["GetCountReasons", "GetTaskEventTypes"];

export const REFERENCE_READ_CACHE_MAX_AGE_SECONDS = 14 * 24 * 60 * 60;

export const REFERENCE_READ_CACHE_MAX_ENTRIES = 20;

export const LOCATION_TREE_CACHE_OPERATIONS: readonly string[] = ["GetSiteLocations", "GetSubLocations"];

export const LOCATION_TREE_READ_CACHE_MAX_AGE_SECONDS = 14 * 24 * 60 * 60;

export const LOCATION_TREE_READ_CACHE_MAX_ENTRIES = 100;

export const NETWORK_FIRST_TIMEOUT_SECONDS = 5;

export const MATERIAL_CATALOGUE_CACHE_OPERATIONS: readonly string[] = ["GetMaterialItems"];

export const MATERIAL_CATALOGUE_READ_CACHE_MAX_AGE_SECONDS = 24 * 60 * 60;

export const MATERIAL_CATALOGUE_READ_CACHE_MAX_ENTRIES = 20;

export const HISTORY_CACHE_OPERATION = "GetInventoryTransactions";

export const HISTORY_CACHE_OPERATIONS: readonly string[] = [HISTORY_CACHE_OPERATION];

export const HISTORY_READ_CACHE_MAX_AGE_SECONDS = 4 * 60 * 60;

export const HISTORY_READ_CACHE_MAX_ENTRIES = 40;

export const QUANTITY_CACHE_OPERATIONS: readonly string[] = ["GetMaterialItemLocationQuantities"];

export const QUANTITY_READ_CACHE_MAX_AGE_SECONDS = 12 * 60 * 60;

export const QUANTITY_READ_CACHE_MAX_ENTRIES = 24;

export const KEY_MISALIGNED_QUANTITY_OPERATIONS: readonly string[] = ["GetInventoryItems"];

export const isKeyMisalignedQuantityOperation = (operationName: string | null | undefined): boolean =>
	typeof operationName === "string" && KEY_MISALIGNED_QUANTITY_OPERATIONS.includes(operationName);

export const isHistoryCacheOperation = (operationName: string | null | undefined): boolean =>
	typeof operationName === "string" && HISTORY_CACHE_OPERATIONS.includes(operationName);

export const isReferenceCacheOperation = (operationName: string | null | undefined): boolean =>
	typeof operationName === "string" && REFERENCE_CACHE_OPERATIONS.includes(operationName);

export const isLocationTreeCacheOperation = (operationName: string | null | undefined): boolean =>
	typeof operationName === "string" && LOCATION_TREE_CACHE_OPERATIONS.includes(operationName);

export const isMaterialCatalogueCacheOperation = (operationName: string | null | undefined): boolean =>
	typeof operationName === "string" && MATERIAL_CATALOGUE_CACHE_OPERATIONS.includes(operationName);

export const READ_CACHE_STRATEGY = {
	NETWORK_FIRST: "networkFirst",
	NETWORK_ONLY: "networkOnly",
	STALE_WHILE_REVALIDATE: "staleWhileRevalidate"
} as const;

export type ReadCacheStrategy = (typeof READ_CACHE_STRATEGY)[keyof typeof READ_CACHE_STRATEGY];

export const readCacheStrategyFor = (operationName: string | null | undefined): ReadCacheStrategy => {
	if (isLocationTreeCacheOperation(operationName) || isMaterialCatalogueCacheOperation(operationName)) {
		return READ_CACHE_STRATEGY.NETWORK_FIRST;
	}
	if (
		isHistoryCacheOperation(operationName) ||
		isReferenceCacheOperation(operationName) ||
		isQuantityCacheOperation(operationName) ||
		isCacheableReadOperation(operationName)
	) {
		return READ_CACHE_STRATEGY.STALE_WHILE_REVALIDATE;
	}
	return READ_CACHE_STRATEGY.NETWORK_ONLY;
};

export const isQuantityCacheOperation = (operationName: string | null | undefined): boolean =>
	typeof operationName === "string" &&
	!isKeyMisalignedQuantityOperation(operationName) &&
	QUANTITY_CACHE_OPERATIONS.includes(operationName);

const isCacheableReadOperation = (operationName: string | null | undefined): boolean =>
	typeof operationName === "string" && CACHEABLE_READ_OPERATIONS.includes(operationName);

export default isCacheableReadOperation;
